import config from '@/config';
import { BusEvent } from '@/models/bus';
import { bus } from '@/bus';
import { ApiBase, ListOptions, InternationalizedField } from '@/api/common';
import ApiList from '@/api/list';
import { Mutation } from '@/store';
import store from '@/store';
import CourseSkill from '@/api/courseSkill';
import VersionDict from '@/api/versionDict';
import { isEmpty } from 'lodash';

const NO_UPLOAD_FIELDS = [
  'SLUG',
  'theme_id',
  'topic_id',
  'submodules',
  'project_id',
  'lessons',
  'bg_image_link',
  'created',
  'updated',
  'module_id',
  'course_skills_objects'
];

export default class Module extends ApiBase<Module> {
  public static readonly SLUG: string = 'module';
  public readonly SLUG: string = 'module';
  private static readonly URL_BASE: string = config.API_ROOT + '/module/';
  private static readonly MASS_IMPORT_URL_BASE =
    config.API_ROOT + '/mass-import/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public created?: string = undefined;
  public updated?: string = undefined;
  public module_id?: string = undefined;
  public project_id?: string = undefined;
  public parent_project?: any = undefined;
  public theme_id?: string = undefined;
  public topic_id?: string = undefined;
  public submodules?: any = undefined;
  public lessons?: any = undefined;
  public bg_image?: number | null = undefined;
  public bg_image_link?: string | null = undefined;
  public banner_image?: number | null = undefined;
  public banner_image_link?: string | null = undefined;
  public minimum_access?: string = undefined;
  public layout?: string = undefined;
  public version?: string = undefined;
  public order?: number = undefined;
  public theme?: number = undefined;
  public topic?: number = undefined;
  public school?: number = undefined;
  public name: InternationalizedField<string> = new InternationalizedField<string>();
  public description: InternationalizedField<string> = new InternationalizedField<string>();
  public country_availability: string[] = [];
  public language_availability?: string[] = [];
  public platform_availability?: string = undefined;
  public version_availability?: VersionDict[] = [];
  public course_skills?: number[] = undefined;
  public course_skills_objects?: CourseSkill[] = [];
  public module_icon?: number | null = undefined;
  public module_icon_link?: string | null = undefined;
  public tags: string[] = [];
  public outcome_tag: string[] = [];
  public is_program: boolean = false;

  // currently unused
  public spreadsheet?: string = undefined;

  public constructor() {
    super({
      urlBase: Module.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });
  }

  public assign(response: any): Module {
    super.assign(response);

    if (response) {
      if ('name' in response) {
        this.name = new InternationalizedField<string>(response.name);
      }
      if ('description' in response) {
        this.description = new InternationalizedField<string>(
          response.description
        );
      }
      if ('country_availability' in response) {
        this.country_availability = [];
        if (!isEmpty(response.country_availability)) {
          response.country_availability.forEach((item: any) => {
            this.country_availability.push(item);
          });
        }
      }
      if ('language_availability' in response) {
        this.language_availability = [];
        if (!isEmpty(response.language_availability)) {
          response.language_availability.forEach((item: any) => {
            this.language_availability.push(item);
          });
        }
      }
      if ('version_availability' in response) {
        this.version_availability = [];
        if (!isEmpty(response.version_availability)) {
          response.version_availability.forEach((item: any) => {
            this.version_availability.push(new VersionDict().assign(item));
          });
        }
      }
      if ('course_skills' in response) {
        this.course_skills = undefined;
        this.course_skills_objects = [];
        if (!isEmpty(response.course_skills)) {
          response.course_skills.forEach((course_skill: any) => {
            this.course_skills_objects.push(
              new CourseSkill().assign(course_skill)
            );
          });
        }
      }

      if ('tags' in response) {
        this.tags = [];
        if (!isEmpty(response.tags)) {
          response.tags.forEach((tag: any) => {
            this.tags.push(tag);
          });
        }
      }

      if ('outcome_tag' in response) {
        this.outcome_tag = [];
        if (!isEmpty(response.outcome_tag)) {
          response.outcome_tag.forEach((tag: any) => {
            this.outcome_tag.push(tag);
          });
        }
      }
    }
    return this;
  }

  public static async get(id: number): Promise<Module> {
    return this.doGet({
      urlBase: Module.URL_BASE,
      id,
      create: (response) => new Module().assign(response)
    });
  }

  public static async list(): Promise<ApiList<Module>> {
    const options: ListOptions<Module> = {
      urlBase: Module.URL_BASE,
      create: (response) => new Module().assign(response)
    };
    return this.doList(options);
  }

  public static async listAll(): Promise<ApiList<Module>> {
    return this.doList({
      urlBase: Module.URL_BASE,
      create: (response) => new Module().assign(response),
      all: true,
      parameters: {
        minimal: true
      },
      readFromCache: () => store.state.moduleList,
      writeToCache: async (list: ApiList<Module>) =>
        await store.commit(Mutation.CACHE_MODULE_LIST, list)
    });
  }

  public async delete(): Promise<void> {
    const id = this.id;
    await super.delete();

    // In case the entity is cached
    await store.commit(Mutation.DELETE_FROM_MODULE_LIST, id);
    bus.$emit(BusEvent.CACHE_CHANGED);
  }

  public async save(): Promise<number> {
    const isNew = !this.id;
    const id = await super.save();
    await this.saveAvailability();
    await this.saveTags();
    await this.saveOutcomeTags();

    if (isNew) {
      await store.commit(Mutation.ADD_TO_MODULE_LIST, this);
    } else {
      await this.saveCourseSkills();
      await super.save();
      await store.commit(Mutation.MODULE_UPDATED, this);
    }
    bus.$emit(BusEvent.CACHE_CHANGED);

    return id;
  }

  public async saveCourseSkills() {
    for (const course_skill of this.course_skills_objects) {
      if (course_skill.state !== 'deleted') {
        course_skill.parent_module = this.id;
        if (!this.course_skills) {
          this.course_skills = [];
        }
        this.course_skills.push(course_skill.id);
      } else if (course_skill.state === 'deleted' && course_skill.id) {
        await course_skill.delete();
      }
    }
  }

  public async saveAvailability() {
    if (this.country_availability && this.country_availability.length === 0) {
      if (!this.id) {
        return delete this.country_availability;
      } else {
        this.country_availability = [];
      }
    }
    if (this.language_availability && this.language_availability.length === 0) {
      if (!this.id) {
        return delete this.language_availability;
      } else {
        this.language_availability = [];
      }
    }
  }

  public getName(): string {
    const name = this.name.getDefaultValue();
    if (name) {
      return name;
    }
    return '';
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }

  public saveTags() {
    if (this.tags && this.tags.length === 0) {
      if (!this.id) {
        return delete this.tags;
      } else {
        this.tags = [];
      }
    }
  }

  public saveOutcomeTags() {
    if (this.outcome_tag && this.outcome_tag.length === 0) {
      if (!this.id) {
        return delete this.outcome_tag;
      } else {
        this.outcome_tag = [];
      }
    }
  }

  public download() {
    return Module.doGet({
      urlBase: Module.MASS_IMPORT_URL_BASE,
      id: null,
      create: (response) => response,
      query: {
        moduleId: this.id
      }
    });
  }
}
